import { useState, useEffect, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { agreements, type AgreementStatus } from '../../api/endpoints';

const CACHE_KEY = 'easi_agreement_status';

// Read sessionStorage cache synchronously so the first render never shows a
// loading screen. This eliminates the visual flash between the HTML splash
// (96px logo + waveform bars) and the caseload.
function readCachedStatus(): AgreementStatus | null {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached) as AgreementStatus;
  } catch {
    sessionStorage.removeItem(CACHE_KEY);
  }
  return null;
}

export default function AgreementGuard({ children }: { children: ReactNode }) {
  const cached = readCachedStatus();
  const [status, setStatus] = useState<AgreementStatus | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If we already have a cached status, still refresh in the background
    // but don't block rendering.
    let cancelled = false;

    async function checkStatus() {
      try {
        const response = await agreements.getStatus();
        const data = response.data;
        if (!cancelled && data) {
          setStatus(data);
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    checkStatus();
    return () => { cancelled = true; };
  }, []);

  // While loading with no cache, render children optimistically.
  // The backend enforces agreement requirements on write operations,
  // and the check completes in <100ms. This prevents any intermediate
  // loading screen from appearing between the HTML splash and the caseload.
  if (loading) {
    return <>{children}</>;
  }

  // On error, let the user through rather than blocking — the backend
  // will enforce agreement requirements on write operations
  if (error || !status) {
    return <>{children}</>;
  }

  // Decision tree:
  // 1. ToS not accepted → redirect to ToS page
  if (!status.tosAccepted) {
    return <Navigate to="/terms-of-service" replace />;
  }

  // 2. ToS accepted but no BAA selection yet → redirect to BAA selection
  if (!status.baaSelectionMade) {
    return <Navigate to="/baa-selection" replace />;
  }

  // 3. BAA required but not accepted → redirect to BAA page
  if (status.baaRequired && !status.baaAccepted) {
    return <Navigate to="/baa" replace />;
  }

  // 4. All good → render children
  return <>{children}</>;
}
